import { usePromptToolboxContext } from '@Context/prompt-toolbox/PromptToolboxContext';
import Divider from './Divider';
import NewPrompt from './NewPrompt';
import PrevPrompt from './PrevPrompt';
import { useUtilityContext } from '@Context/prompt-toolbox/UtilityContext';
import React, { useState } from 'react';
import { Check, PencilSimple, Sidebar, Trash, X } from '@phosphor-icons/react';
import { BounceLoader } from 'react-spinners';
import colors from '@Common/assets/colors';

interface Props {
  toggled: boolean;
  onToggleSidebar: () => void;
}

const DiffMergeView: React.FC<Props> = ({ toggled, onToggleSidebar }) => {
  const { selectedPromptVersion, renamePrompt } = usePromptToolboxContext();
  const { loaders, setModal } = useUtilityContext();
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState('');

  const disable = !selectedPromptVersion;

  const handleRenameClick = () => {
    if (selectedPromptVersion) {
      setNewName(selectedPromptVersion.name);
      setIsRenaming(true);
    }
  };

  const handleConfirmRename = async () => {
    //call api
    setIsRenaming(false);
    await renamePrompt(newName);
    console.log('New Name:', newName);
  };

  const handleCancelRename = () => {
    setIsRenaming(false);
  };

  const isLoading =
    loaders.deletePrompt || loaders.deleteVersion || loaders.newVersion;

  return (
    <div className="flex h-full w-full flex-col">
      <div
        className={`flex flex-row items-center gap-x-4 transition-all duration-300 ease-in-out ${toggled ? 'md:rounded-tr-2xl' : 'md:rounded-t-2xl'} bg-primary-700 px-6 py-4`}
      >
        <button onClick={onToggleSidebar}>
          <Sidebar size={25} weight="fill" className="text-white" />
        </button>
        <div className="flex w-full flex-row items-center justify-between gap-x-4">
          <span className="group flex min-w-0 flex-1 flex-row items-center gap-x-3 font-inter font-semibold tracking-widest text-white">
            {/* TITLE or INPUT */}
            {isRenaming ? (
              <div className="flex flex-1 items-center gap-x-2">
                <input
                  data-testid="rename-text-area"
                  maxLength={150}
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value.trimStart())}
                  className="min-w-0 flex-1 rounded-md px-2 py-1 text-black focus:outline-none"
                />
                <button
                  data-testid="confirm-rename-button"
                  disabled={
                    newName === selectedPromptVersion?.name || newName === ''
                  }
                  onClick={handleConfirmRename}
                  className="flex-shrink-0 text-brandSuccess-400 disabled:text-gray-500"
                >
                  <Check size={20} weight="bold" />
                </button>
                <button
                  data-testid="cancel-rename-button"
                  onClick={handleCancelRename}
                  className="flex-shrink-0 text-brandRed-300"
                >
                  <X size={20} weight="bold" />
                </button>
              </div>
            ) : (
              <>
                <label
                  data-testid="prompt-title"
                  className="max-w-md truncate"
                  title={
                    selectedPromptVersion
                      ? selectedPromptVersion.name
                      : 'PROMPT TOOLBOX V 0.1'
                  }
                >
                  {selectedPromptVersion
                    ? selectedPromptVersion.name.toUpperCase()
                    : 'PROMPT TOOLBOX V 0.1'}
                </label>

                {loaders.renamePrompt && (
                  <BounceLoader size={20} color={colors.brandWarning[300]} />
                )}

                {/* RENAME */}
                {selectedPromptVersion && !loaders.renamePrompt && (
                  <label
                    data-testid="rename-button"
                    className="ml-2 hidden flex-shrink-0 cursor-pointer group-hover:block"
                    onClick={handleRenameClick}
                  >
                    <PencilSimple size={20} weight="fill" />
                  </label>
                )}
              </>
            )}
          </span>
          {selectedPromptVersion && (
            <div className="flex-shrink-0">
              {loaders.deletePrompt ? (
                <div className="flex flex-row items-center gap-x-2">
                  <label className="animate-pulse whitespace-nowrap text-sm tracking-wide text-white">
                    Deleting Prompt
                  </label>
                  <BounceLoader size={28} color={colors.brandWarning[300]} />
                </div>
              ) : (
                <div className="flex flex-row gap-x-2 text-sm">
                  <button
                    data-testid="delete-button"
                    disabled={disable || isLoading}
                    className="flex flex-row items-center gap-x-1 whitespace-nowrap rounded-md bg-brandRed-300 px-3 py-1 text-white disabled:bg-gray-500"
                    onClick={() => setModal('deletePrompt', true)}
                  >
                    <Trash size={20} />
                    <label className="cursor-pointer">Delete</label>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div
        className={`custom-scroll relative flex h-full w-full flex-row gap-x-2 overflow-x-auto transition-all duration-300 ease-in-out ${toggled ? 'rounded-br-2xl' : 'rounded-b-2xl'} border-2 border-gray-200 p-2`}
      >
        {isLoading && (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-y-3 rounded-br-2xl"></div>
        )}
        {/* PREV PROMPT */}
        <PrevPrompt />

        {/* DIVIDER */}
        <Divider />

        {/* NEW PROMPT */}
        <NewPrompt />
      </div>
    </div>
  );
};

export default DiffMergeView;
